import React from 'react';
import { useTheme } from 'styled-components';
import { IconSpinner } from '../../icons/spinner/spinner';
import {
    IconButtonSizeType,
    IconButtonVariantType,
    IconButtonVariationType,
} from '../../../../theme/type/icon-button-type';
import { BreakpointSizeType } from '../../../../theme/type/breakpoints-type';
import { breakpointPrefix, cn } from '../../../../utils/tailwind';

type IconButtonResponsiveSize = Partial<Record<BreakpointSizeType, IconButtonSizeType>>;

const iconButtonSizeClass: Record<IconButtonSizeType, string> = {
    xsmall: 'h-[3rem] w-[3rem] min-w-[3rem] [&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:min-w-[1.2rem]',
    vsmall: 'h-[3.2rem] w-[3.2rem] min-w-[3.2rem] [&_i]:h-[1.4rem] [&_i]:w-[1.4rem] [&_i]:min-w-[1.4rem]',
    small: 'h-[3.6rem] w-[3.6rem] min-w-[3.6rem] [&_i]:h-[1.6rem] [&_i]:w-[1.6rem] [&_i]:min-w-[1.6rem]',
    medium: 'h-[4rem] w-[4rem] min-w-[4rem] [&_i]:h-[1.8rem] [&_i]:w-[1.8rem] [&_i]:min-w-[1.8rem]',
    large: 'h-[4.8rem] w-[4.8rem] min-w-[4.8rem] [&_i]:h-[2.4rem] [&_i]:w-[2.4rem] [&_i]:min-w-[2.4rem]',
    vlarge: 'h-[5.2rem] w-[5.2rem] min-w-[5.2rem] [&_i]:h-[2.8rem] [&_i]:w-[2.8rem] [&_i]:min-w-[2.8rem]',
    xlarge: 'h-[6.2rem] w-[6.2rem] min-w-[6.2rem] [&_i]:h-[3.8rem] [&_i]:w-[3.8rem] [&_i]:min-w-[3.8rem]',
};

const getResponsiveSizeClasses = (size: IconButtonResponsiveSize) => {
    const entries = Object.entries(size) as [BreakpointSizeType, IconButtonSizeType][];

    return entries.map(([breakpoint, value], index) => {
        const className = iconButtonSizeClass[value];

        return index === 0
            ? className
            : className
                  .split(' ')
                  .map((item) => `${breakpointPrefix[breakpoint]}${item}`)
                  .join(' ');
    });
};

interface ButtonIconPropsInterface {
    icon?: React.ReactNode;
    size?: IconButtonSizeType | IconButtonResponsiveSize;
    variation?: IconButtonVariationType;
    variant?: IconButtonVariantType;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement & HTMLDivElement>) => void;
    disabled?: boolean;
    loading?: boolean;
}

export const IconButton: React.FC<ButtonIconPropsInterface> = ({
    icon,
    variant = 'default',
    size = 'medium',
    variation = 'filled',
    onClick,
    disabled = false,
    loading = false,
}) => {
    const theme = useTheme();
    const state = theme.iconButton.variant[variant][variation];

    let sizeClassName = iconButtonSizeClass.medium;

    if (typeof size === 'string') {
        sizeClassName = iconButtonSizeClass[size];
    } else if (size) {
        sizeClassName = getResponsiveSizeClasses(size).join(' ');
    }

    const baseState = disabled ? state.disabled : state.base;
    const hoverState = !disabled && onClick ? state.hover : baseState;
    const activeState = !disabled && onClick ? state.active : baseState;

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement & HTMLDivElement>) => {
                if (!disabled && onClick) {
                    onClick(e);
                }
            }}
            disabled={disabled}
            type="button"
            className={cn(
                'relative inline-flex shrink-0 items-center justify-center rounded-full border border-solid transition-all duration-200 ease-in-out',
                'text-[var(--ib-color)] bg-[var(--ib-bg)] border-[var(--ib-border)]',
                'hover:text-[var(--ib-hover-color)] hover:bg-[var(--ib-hover-bg)] hover:border-[var(--ib-hover-border)]',
                'active:text-[var(--ib-active-color)] active:bg-[var(--ib-active-bg)] active:border-[var(--ib-active-border)]',
                disabled ? 'cursor-not-allowed' : 'cursor-default',
                !disabled && onClick && 'cursor-pointer',
                sizeClassName,
            )}
            style={
                {
                    '--ib-color': baseState.color,
                    '--ib-bg': baseState.background,
                    '--ib-border': baseState.border,
                    '--ib-hover-color': hoverState.color,
                    '--ib-hover-bg': hoverState.background,
                    '--ib-hover-border': hoverState.border,
                    '--ib-active-color': activeState.color,
                    '--ib-active-bg': activeState.background,
                    '--ib-active-border': activeState.border,
                } as React.CSSProperties
            }
        >
            {loading ? <IconSpinner /> : icon}
        </button>
    );
};
